import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Avatar,
  Box,
  Chip,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import PhoneIcon from '@mui/icons-material/Phone'
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback'
import AssignmentIcon from '@mui/icons-material/Assignment'
import EventIcon from '@mui/icons-material/Event'
import EmailIcon from '@mui/icons-material/Email'
import { DataTable, type Column } from '../components/ui/DataTable'
import { EmailDetailModal } from '../components/shared/EmailDetailModal'
import { CallDetailModal } from '../components/shared/CallDetailModal'
import { callLogsApi, formatDuration, formatCallTime, CALL_STATUS_COLORS, CALL_STATUS_LABELS, CALL_OUTCOME_LABELS, CALL_OUTCOMES, type CallLog, type CallLogFilters, getCallStatusDetailText } from '../lib/api/callLogsApi'
import { leadFollowUpsApi, type LeadFollowUp, type FollowUpFilters } from '../lib/api/leadFollowUpsApi'
import { leadTasksApi, type LeadTask, type LeadTaskFilters } from '../lib/api/leadTasksApi'
import { tasksApi, type Task } from '../lib/api/tasksApi'
import { themeColors } from '../app/layout/theme'
import { emailApi, type EmailLog, type EmailLogFilters } from '../lib/api/emailApi'
import { PageHeader } from '../components/shared/PageHeader'
import { ListToolbar } from '../components/shared/ListToolbar'
import { usePermission } from '../lib/hooks/usePermission'

import { authStore } from '../lib/stores/authStore'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'
import { orgsApi, lobsApi } from '../lib/api/tenantsApi'
import {
  UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef,
  applyUnifiedDateFilter, countActiveFilters, dateRangeForPreset,
} from '../components/shared/UnifiedFilterDialog'

// survives a page refresh within the same browser tab.
function useSessionState<T>(key: string, initial: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(key)
      return stored !== null ? (JSON.parse(stored) as T) : initial
    } catch {
      return initial
    }
  })

  const setAndPersist: React.Dispatch<React.SetStateAction<T>> = (value) => {
    setState(prev => {
      const next = typeof value === 'function' ? (value as (p: T) => T)(prev) : value
      try { sessionStorage.setItem(key, JSON.stringify(next)) } catch { /* quota exceeded – ignore */ }
      return next
    })
  }

  return [state, setAndPersist]
}

// ─── Org/LOB tier helper (mirrors backend ITenantContext convention) ──────────
// Shared across all Activity tabs: SuperAdmin gets Organisation + LOB pickers,
// Org Admin (org fixed, no lobId of their own) gets LOB-only, Normal User gets neither.
function useTenantTier() {
  // SuperAdmin detection: OrgId/LobId-based (matches the navbar org selector), not
  // usePermission's roleName-string match — that doesn't reliably match every
  // SuperAdmin's role label, which previously hid the Organisation/LOB filters here.
  const { isSuperAdmin } = useOrgSelector()
  const userOrgId = authStore.user?.orgId ?? ''
  const userLobId = authStore.user?.lobId ?? ''
  const isOrgAdmin = !isSuperAdmin && !!userOrgId && !userLobId
  return { isSuperAdmin, isOrgAdmin, userOrgId }
}

function useOrgLobFilterData(filters: UnifiedFilters, isSuperAdmin: boolean, isOrgAdmin: boolean, userOrgId: string) {
  const { data: orgs = [] } = useQuery({ queryKey: ['admin-orgs'], queryFn: orgsApi.getAll, enabled: isSuperAdmin, staleTime: 60_000 })
  const navOrgFilter = useOrgFilter()
  // Prefer the org picked in this page's own filter dialog; fall back to whatever
  // org is selected in the navbar so the LOB list works right after picking an org
  // up top, without needing to also set the local Organisation filter.
  const orgIdForFetching = filters.checkboxes.orgId?.[0] || navOrgFilter.orgId
  const lobOrgIdForFetching = isSuperAdmin ? orgIdForFetching : userOrgId
  const { data: lobsForFilter = [] } = useQuery({
    queryKey: ['admin-lobs', lobOrgIdForFetching],
    queryFn: () => lobsApi.getAll(lobOrgIdForFetching!),
    enabled: (isSuperAdmin || isOrgAdmin) && !!lobOrgIdForFetching,
    staleTime: 30_000,
  })
  const effectiveOrgIds = isSuperAdmin && filters.checkboxes.orgId?.length ? filters.checkboxes.orgId : undefined
  const effectiveLobIds = (isSuperAdmin || isOrgAdmin) && filters.checkboxes.lobId?.length ? filters.checkboxes.lobId : undefined
  return { orgs, lobsForFilter, effectiveOrgIds, effectiveLobIds }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function fmtDateTime(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}

function calcDuration(start: string, end: string | null): string {
  if (!end) return '—'
  const diffMs = new Date(end).getTime() - new Date(start).getTime()
  if (diffMs <= 0) return '—'
  const mins = Math.floor(diffMs / 60000)
  const hrs = Math.floor(mins / 60)
  if (hrs > 0) return `${hrs}h ${mins % 60}m`
  return `${mins}m`
}

function getEventSortRank(ev: LeadFollowUp, now: number) {
  const scheduledAt = new Date(ev.scheduledAt).getTime()
  const isPending = ev.status === 'PENDING'
  const isUpcomingPending = isPending && scheduledAt >= now

  if (isUpcomingPending) return 0
  return 1
}

function sortEventsForActivity(events: LeadFollowUp[]) {
  const now = Date.now()
  return [...events].sort((a, b) => {
    const rankA = getEventSortRank(a, now)
    const rankB = getEventSortRank(b, now)

    if (rankA !== rankB) return rankA - rankB

    const timeA = new Date(a.scheduledAt).getTime()
    const timeB = new Date(b.scheduledAt).getTime()

    // Rank 0: Upcoming pending -> nearest first (ascending)
    if (rankA === 0) {
      return timeA - timeB
    }

    // Rank 1: Past / non-pending -> most recent first (descending)
    return timeB - timeA
  })
}

function parseLocalDate(dateStr: string) {
  const [yyyy, mm, dd] = dateStr.split('-').map(Number)
  return new Date(yyyy, mm - 1, dd)
}

function resolveDateRange(preset: string, dateFrom?: string, dateTo?: string) {
  if (!preset) return { from: undefined, to: undefined }

  if (preset === 'CUSTOM') {
    const fromDate = dateFrom ? parseLocalDate(dateFrom) : undefined
    if (fromDate) fromDate.setHours(0, 0, 0, 0)
    const toDate = dateTo ? parseLocalDate(dateTo) : undefined
    if (toDate) toDate.setHours(23, 59, 59, 999)
    return {
      from: fromDate ? fromDate.toISOString() : undefined,
      to: toDate ? toDate.toISOString() : undefined,
    }
  }

  // All other presets (TODAY, YESTERDAY, LAST_7, A_MONTH_AGO, 3_MONTHS, 6_MONTHS, 1_YEAR)
  // are handled by the canonical helper exported from UnifiedFilterDialog.
  const { from, to } = dateRangeForPreset(preset)
  return { from: from.toISOString(), to: to.toISOString() }
}

function UserCell({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar src={avatarUrl ?? undefined} sx={{ width: 24, height: 24, fontSize: 11, background: themeColors.primary }}>
        {name?.[0]?.toUpperCase()}
      </Avatar>
      <Typography sx={{ fontSize: 13, color: '#2F2F3A' }}>{name}</Typography>
    </Box>
  )
}

function StatusChip({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <Chip label={label} size="small" sx={{
      fontSize: 11, fontWeight: 600, height: 20,
      bgcolor: bg, color,
    }} />
  )
}

// ─── Call Status / Outcome colors ─────────────────────────────────────────────

const CALL_STATUS_BG: Record<string, string> = {
  INITIATED: '#f3f4f6',
  CONNECTED: '#f0fdf4',
  NOT_ANSWERED: '#fef3c7',
  BUSY: '#fef3c7',
  FAILED: '#fef2f2',
  MISSED: '#f3f4f6',
}

// ─── Task Status helpers ───────────────────────────────────────────────────────

const TASK_STATUS: Record<string, { label: string; color: string; bg: string }> = {
  PENDING: { label: 'Pending', color: '#6b7280', bg: '#f3f4f6' },
  IN_PROGRESS: { label: 'In Progress', color: '#0070d2', bg: '#e8f4fd' },
  DONE: { label: 'Done', color: '#16a34a', bg: '#f0fdf4' },
  CANCELLED: { label: 'Cancelled', color: '#9ca3af', bg: '#f9fafb' },
}

// ─── Follow-up Status / Type helpers ─────────────────────────────────────────

const FU_STATUS: Record<string, { label: string; color: string; bg: string }> = {
  PENDING: { label: 'Pending', color: '#f59e0b', bg: '#fef3c7' },
  DONE: { label: 'Done', color: '#16a34a', bg: '#f0fdf4' },
  CANCELLED: { label: 'Cancelled', color: '#9ca3af', bg: '#f9fafb' },
}

const FU_TYPE_LABELS: Record<string, string> = {
  CALL: 'Call',
  MEETING: 'Meeting',
  DEMO: 'Demo',
  SITE_VISIT: 'Site Visit',
  OTHER: 'Other',
}

// Removed manual TabToolbar in favor of ListToolbar

// ─── Filter definitions per tab ───────────────────────────────────────────────

const BASE_CALL_FILTER_DEFS: FilterDef[] = [
  {
    type: 'checkbox', key: 'callType', label: 'Call Type', options: [
      { label: 'Outgoing', value: 'OUTGOING' },
      { label: 'Incoming', value: 'INCOMING' },
    ]
  },
  {
    // Status options are populated dynamically when the filter dialog opens
    // (lazy fetch from /calls/statuses) — empty here as a safe default.
    type: 'checkbox', key: 'status', label: 'Status', options: []
  },
  {
    // Outcomes are a closed enum — no API needed, always static.
    type: 'checkbox', key: 'outcome', label: 'Outcome',
    options: CALL_OUTCOMES.map(o => ({ label: CALL_OUTCOME_LABELS[o], value: o }))
  },
  {
    type: 'checkbox', key: 'relatedTo', label: 'Related To', options: [
      { label: 'Lead', value: 'LEAD' },
      { label: 'Contact', value: 'CONTACT' },
      { label: 'Deal', value: 'DEAL' },
      { label: 'Sales', value: 'SALES' },
      { label: 'Retention', value: 'RETENTION' },
    ]
  },
  { type: 'date', label: 'Date' },
  { type: 'owner', label: 'Owner' },
]

const BASE_TASK_FILTER_DEFS: FilterDef[] = [
  {
    type: 'checkbox', key: 'status', label: 'Status', options: [
      { label: 'Pending', value: 'PENDING' },
      { label: 'In Progress', value: 'IN_PROGRESS' },
      { label: 'Done', value: 'DONE' },
      { label: 'Cancelled', value: 'CANCELLED' },
    ]
  },
  { type: 'date', label: 'Date' },
  { type: 'owner', label: 'Owner' },
]

const BASE_EVENT_FILTER_DEFS: FilterDef[] = [
  {
    type: 'checkbox', key: 'type', label: 'Event Type', options: [
      { label: 'Call', value: 'CALL' },
      { label: 'Meeting', value: 'MEETING' },
      { label: 'Demo', value: 'DEMO' },
      { label: 'Site Visit', value: 'SITE_VISIT' },
      { label: 'Other', value: 'OTHER' },
    ]
  },
  {
    type: 'checkbox', key: 'status', label: 'Status', options: [
      { label: 'Pending', value: 'PENDING' },
      { label: 'Done', value: 'DONE' },
      { label: 'Cancelled', value: 'CANCELLED' },
    ]
  },
  { type: 'date', label: 'Date' },
  { type: 'owner', label: 'Owner' },
]

const BASE_EMAIL_FILTER_DEFS: FilterDef[] = [
  {
    type: 'checkbox', key: 'status', label: 'Status', options: [
      { label: 'Sent', value: 'SENT' },
      { label: 'Draft', value: 'DRAFT' },
      { label: 'Failed', value: 'FAILED' },
    ]
  },
  { type: 'date', label: 'Date' },
  { type: 'owner', label: 'Owner' },
]

// ─── Tab panels ───────────────────────────────────────────────────────────────

interface TabProps {
  page: number
  setPage: (page: number) => void
  pageSize: number
  onPageSizeChange: (pageSize: number) => void
}

interface CallTabProps extends TabProps {
  onRowClick: (row: CallLog) => void
}

function CallTab({ onRowClick, page, setPage, pageSize, onPageSizeChange }: CallTabProps) {
  const navigate = useNavigate()
  const [search, setSearch] = useSessionState('activity_calls_search', '')
  const [filters, setFilters] = useSessionState<UnifiedFilters>('activity_calls_filters', EMPTY_UNIFIED_FILTERS)
  // Track whether the filter dialog has ever been opened this session so we
  // trigger the status fetch lazily (only on first open) rather than on mount.
  const [statusFetchEnabled, setStatusFetchEnabled] = useState(false)

  const { isSuperAdmin, isOrgAdmin, userOrgId } = useTenantTier()
  const { orgs, lobsForFilter, effectiveOrgIds, effectiveLobIds } = useOrgLobFilterData(filters, isSuperAdmin, isOrgAdmin, userOrgId)

  // Lazy fetch: only fires when the user opens the filter dialog for the first time.
  // Results are cached by React Query for 5 minutes — no localStorage needed.
  const { data: liveStatuses = [] } = useQuery({
    queryKey: ['call-statuses'],
    queryFn: callLogsApi.getStatuses,
    enabled: statusFetchEnabled,
    staleTime: 5 * 60 * 1000,
  })

  // Call logs have no LobId column of their own — LOB is derived server-side from
  // the related Lead/Contact/Deal, but the filter UI works the same as elsewhere.
  const filterDefs: FilterDef[] = useMemo(() => {
    const isNormalUser = !isSuperAdmin && !isOrgAdmin && !authStore.canViewLobWideData
    const defs: FilterDef[] = BASE_CALL_FILTER_DEFS.map(d => {
      if (d.type === 'checkbox' && d.key === 'status') {
        // Populate with live DB statuses once fetched; fall back to a minimal
        // hardcoded list so the filter isn't empty before the first open.
        const fallback = [
          { label: 'Connected', value: 'CONNECTED' },
          { label: 'Not Answered', value: 'NOT_ANSWERED' },
          { label: 'Busy', value: 'BUSY' },
          { label: 'Failed', value: 'FAILED' },
          { label: 'Initiated', value: 'INITIATED' },
        ]
        const options = liveStatuses.length > 0
          ? liveStatuses.map(s => ({ label: CALL_STATUS_LABELS[s] ?? s.replace(/_/g, ' '), value: s }))
          : fallback
        return { ...d, options }
      }
      return d
    }).filter(d => isNormalUser ? d.type !== 'owner' : true)

    if (isSuperAdmin) {
      defs.push(
        { type: 'checkbox', key: 'orgId', label: 'Organisation', options: orgs.map(o => ({ label: o.name, value: o.id })) },
        { type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) },
      )
    } else if (isOrgAdmin) {
      defs.push({ type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) })
    }
    return defs
  }, [isSuperAdmin, isOrgAdmin, orgs, lobsForFilter, liveStatuses])

  useEffect(() => {
    setPage(1)
  }, [search, filters, setPage])

  const apiFilters: CallLogFilters = useMemo(() => {
    const resolved = resolveDateRange(filters.datePreset, filters.dateFrom, filters.dateTo)
    return {
      search: search || undefined,
      callTypes: filters.checkboxes.callType?.length > 0 ? filters.checkboxes.callType : undefined,
      statuses: filters.checkboxes.status?.length > 0 ? filters.checkboxes.status : undefined,
      outcomes: filters.checkboxes.outcome?.length > 0 ? filters.checkboxes.outcome : undefined,
      relatedTo: filters.checkboxes.relatedTo?.length > 0 ? filters.checkboxes.relatedTo : undefined,
      ownerIds: filters.ownerIds?.length > 0 ? filters.ownerIds : undefined,
      orgIds: effectiveOrgIds,
      lobIds: effectiveLobIds,
      from: resolved.from,
      to: resolved.to,
    }
  }, [search, filters, effectiveOrgIds, effectiveLobIds])

  const { data, isLoading } = useQuery({
    queryKey: ['activity-calls', page, pageSize, apiFilters],
    queryFn: () => callLogsApi.getPagedForActivity(page, pageSize, apiFilters),
    staleTime: 30_000,
  })

  const calls = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const columns: Column[] = [
    {
      id: 'leadName',
      label: 'Page',
      minWidth: 160,
      render: (_: any, row: CallLog) => {
        // A call may be linked to a Lead, a Contact, or a Deal. For deal calls, show
        // the actual person on the deal (Deal.Contact) rather than the deal's title —
        // only fall back to the deal title itself if the deal has no contact at all.
        const personName = row.leadName || row.contactName || row.dealContactName
        const name = personName || row.dealTitle
        if (!name) {
          return <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>—</Typography>
        }
        const isDealNameOnly = !personName && !!row.dealTitle
        return (
          <Typography
            sx={{ fontSize: 13, color: isDealNameOnly ? '#ca8a04' : themeColors.primary, fontWeight: 500 }}
          >
            {name}
          </Typography>
        )
      },
    },
    {
      id: 'relatedTo',
      label: 'Related To',
      minWidth: 160,
      render: (_: any, row: CallLog) => {
        const links = []
        if (row.leadId) {
          links.push(
            <Typography
              key="lead"
              component="span"
              sx={{ fontSize: 13, color: themeColors.primary, cursor: 'pointer', textDecoration: 'underline' }}
              onClick={(e) => { e.stopPropagation(); navigate(`/leads/${row.leadId}`) }}
            >
              Lead
            </Typography>
          )
        }
        if (row.contactId) {
          links.push(
            <Typography
              key="contact"
              component="span"
              sx={{ fontSize: 13, color: themeColors.primary, cursor: 'pointer', textDecoration: 'underline' }}
              onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${row.contactId}`) }}
            >
              Contact
            </Typography>
          )
        }
        if (row.dealId) {
          links.push(
            <Typography
              key="deal"
              component="span"
              sx={{ fontSize: 13, color: themeColors.primary, cursor: 'pointer', textDecoration: 'underline' }}
              onClick={(e) => { e.stopPropagation(); navigate(`/deals/${row.dealId}`) }}
            >
              Deal
            </Typography>
          )
        }
        if (row.salesId) {
          links.push(
            <Typography
              key="sales"
              component="span"
              sx={{ fontSize: 13, color: themeColors.primary, cursor: 'pointer', textDecoration: 'underline' }}
              onClick={(e) => { e.stopPropagation(); navigate(`/sales/${row.salesId}`) }}
            >
              Sales
            </Typography>
          )
        }
        if (row.retentionCustomerId) {
          links.push(
            <Typography
              key="retention"
              component="span"
              sx={{ fontSize: 13, color: themeColors.primary, cursor: 'pointer', textDecoration: 'underline' }}
              onClick={(e) => { e.stopPropagation(); navigate(`/customer-retention/${row.retentionCustomerId}`) }}
            >
              Retention Customer
            </Typography>
          )
        }

        if (links.length === 0) return <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>—</Typography>

        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {links.map((link, i) => (
              <span key={i}>
                {i > 0 && <span style={{ color: '#9ca3af', marginRight: 4 }}>,</span>}
                {link}
              </span>
            ))}
          </Box>
        )
      }
    },
    {
      id: 'callType',
      label: 'Call Type',
      minWidth: 110,
      render: (_: any, row: CallLog) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {row.callType === 'OUTGOING'
            ? <PhoneIcon sx={{ fontSize: 14, color: '#0070d2' }} />
            : <PhoneCallbackIcon sx={{ fontSize: 14, color: '#16a34a' }} />
          }
          <Typography sx={{ fontSize: 13, color: '#2F2F3A' }}>
            {row.callType === 'OUTGOING' ? 'Outgoing' : 'Incoming'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'startedAt',
      label: 'Call Start Time',
      minWidth: 170,
      render: (_: any, row: CallLog) => (
        <Typography sx={{ fontSize: 13, color: '#2F2F3A' }}>{formatCallTime(row.startedAt)}</Typography>
      ),
    },
    {
      id: 'durationSeconds',
      label: 'Duration',
      minWidth: 90,
      render: (_: any, row: CallLog) => (
        <Typography sx={{ fontSize: 13, color: '#2F2F3A' }}>{formatDuration(row.durationSeconds)}</Typography>
      ),
    },
    {
      id: 'initiatedBy',
      label: 'Owner',
      minWidth: 150,
      render: (_: any, row: CallLog) => (
        <UserCell name={row.initiatedBy.name} avatarUrl={row.initiatedBy.avatarUrl} />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 155,
      render: (_: any, row: CallLog) => {
        const detailText = getCallStatusDetailText(row)
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StatusChip
                label={row.status.replace('_', ' ')}
                color={CALL_STATUS_COLORS[row.status] ?? '#6b7280'}
                bg={CALL_STATUS_BG[row.status] ?? '#f3f4f6'}
              />
            </Box>
            {detailText && (
              <Typography sx={{ fontSize: '10.5px', color: '#b91c1c', fontWeight: 600 }}>
                ⚠️ {detailText}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      id: 'outcome',
      label: 'Outcome',
      minWidth: 130,
      render: (_: any, row: CallLog) => row.outcome
        ? <Typography sx={{ fontSize: 13, color: '#2F2F3A' }}>{CALL_OUTCOME_LABELS[row.outcome as keyof typeof CALL_OUTCOME_LABELS] ?? row.outcome}</Typography>
        : <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>—</Typography>,
    },
  ]

  return (
    <>
      <ListToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search contact name, owner…"
        filterDefs={filterDefs}
        filters={filters}
        onFilterChange={setFilters}
        onFilterOpen={() => setStatusFetchEnabled(true)}
      />
      <Box sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataTable
          columns={columns}
          rows={calls}
          isLoading={isLoading}
          rowKey={r => r.id}
          emptyMessage="No call activity yet"
          emptyHint="Calls initiated from lead profiles will appear here"
          emptyIcon={<PhoneIcon />}
          selectable={false}
          showRowNumbers={false}
          onRowClick={onRowClick}
          pagination={{
            page,
            pageSize,
            total,
            totalPages,
            onPageChange: setPage,
            onPageSizeChange,
            pageSizeOptions: [10, 20, 50, 100]
          }}
        />
      </Box>
    </>
  )
}

interface EmailTabProps extends TabProps {
  onRowClick: (row: EmailLog) => void
}

function EmailTab({ onRowClick, page, setPage, pageSize, onPageSizeChange }: EmailTabProps) {
  const [search, setSearch] = useSessionState('activity_emails_search', '')
  const [filters, setFilters] = useSessionState<UnifiedFilters>('activity_emails_filters', EMPTY_UNIFIED_FILTERS)

  const { isSuperAdmin, isOrgAdmin, userOrgId } = useTenantTier()
  const { orgs, lobsForFilter, effectiveOrgIds, effectiveLobIds } = useOrgLobFilterData(filters, isSuperAdmin, isOrgAdmin, userOrgId)

  // Email logs have no LobId column of their own — LOB is derived server-side from
  // the linked Lead/Contact/Customer/Deal, but the filter UI works the same as elsewhere.
  const filterDefs: FilterDef[] = useMemo(() => {
    const isNormalUser = !isSuperAdmin && !isOrgAdmin && !authStore.canViewLobWideData
    const defs = BASE_EMAIL_FILTER_DEFS.filter(d => isNormalUser ? d.type !== 'owner' : true)
    if (isSuperAdmin) {
      defs.push(
        { type: 'checkbox', key: 'orgId', label: 'Organisation', options: orgs.map(o => ({ label: o.name, value: o.id })) },
        { type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) },
      )
    } else if (isOrgAdmin) {
      defs.push({ type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) })
    }
    return defs
  }, [isSuperAdmin, isOrgAdmin, orgs, lobsForFilter])

  useEffect(() => {
    setPage(1)
  }, [search, filters, setPage])

  const apiFilters: EmailLogFilters = useMemo(() => {
    const resolved = resolveDateRange(filters.datePreset, filters.dateFrom, filters.dateTo)
    return {
      search: search || undefined,
      statuses: filters.checkboxes.status?.length > 0 ? filters.checkboxes.status : undefined,
      ownerIds: filters.ownerIds?.length > 0 ? filters.ownerIds : undefined,
      orgIds: effectiveOrgIds,
      lobIds: effectiveLobIds,
      from: resolved.from,
      to: resolved.to,
    }
  }, [search, filters, effectiveOrgIds, effectiveLobIds])

  const { data, isLoading } = useQuery({
    queryKey: ['activity-emails', page, pageSize, apiFilters],
    queryFn: () => emailApi.getPagedForActivity(page, pageSize, apiFilters),
    staleTime: 30_000,
  })

  const emails = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const columns: Column[] = [
    {
      id: 'subject',
      label: 'Subject',
      minWidth: 200,
      render: (_: any, row: EmailLog) => (
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2F2F3A' }}>{row.subject}</Typography>
      ),
    },
    {
      id: 'to',
      label: 'To',
      minWidth: 180,
      render: (_: any, row: EmailLog) => (
        <Typography sx={{ fontSize: 13, color: '#2F2F3A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.to.join(', ')}
        </Typography>
      ),
    },
    {
      id: 'sentAt',
      label: 'Sent At',
      minWidth: 160,
      render: (_: any, row: EmailLog) => (
        <Typography sx={{ fontSize: 13, color: '#2F2F3A' }}>{fmtDateTime(row.sentAt || row.createdAt)}</Typography>
      ),
    },
    {
      id: 'sentByName',
      label: 'Sent By',
      minWidth: 140,
      render: (_: any, row: EmailLog) => (
        <UserCell name={row.sentByName || 'System'} />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (_: any, row: EmailLog) => {
        const sc = row.status === 'SENT' ? { label: 'Sent', color: '#16a34a', bg: '#f0fdf4' } :
          row.status === 'FAILED' ? { label: 'Failed', color: '#dc2626', bg: '#fef2f2' } :
            { label: 'Draft', color: '#f59e0b', bg: '#fef3c7' }
        return <StatusChip label={sc.label} color={sc.color} bg={sc.bg} />
      },
    },
  ]

  return (
    <>
      <ListToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search subject, recipient…"
        filterDefs={filterDefs}
        filters={filters}
        onFilterChange={setFilters}
      />
      <Box sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataTable
          columns={columns}
          rows={emails}
          isLoading={isLoading}
          rowKey={r => r.id}
          emptyMessage="No email activity yet"
          emptyHint="Emails sent from lead or contact profiles will appear here"
          emptyIcon={<EmailIcon />}
          selectable={false}
          showRowNumbers={false}
          onRowClick={onRowClick}
          pagination={{
            page,
            pageSize,
            total,
            totalPages,
            onPageChange: setPage,
            onPageSizeChange,
            pageSizeOptions: [10, 20, 50, 100]
          }}
        />
      </Box>
    </>
  )
}

function sortTasksForActivity(tasks: LeadTask[]) {
  const now = Date.now()
  return [...tasks].sort((a, b) => {
    const rankA = getTaskSortRank(a, now)
    const rankB = getTaskSortRank(b, now)

    if (rankA !== rankB) return rankA - rankB

    const timeA = a.dueDate ? new Date(a.dueDate).getTime() : 0
    const timeB = b.dueDate ? new Date(b.dueDate).getTime() : 0

    // Rank 0 (Today) and Rank 1 (Upcoming) -> nearest first (ascending)
    if (rankA === 0 || rankA === 1) {
      return timeA - timeB
    }

    // Rank 2 (Overdue) and Rank 3 (Done/Cancelled) -> most recent first (descending)
    return timeB - timeA
  })
}

function getTaskSortRank(task: LeadTask, now: number) {
  const isPending = task.status === 'PENDING' || task.status === 'IN_PROGRESS'
  const dueDate = task.dueDate ? new Date(task.dueDate) : null

  if (!isPending) return 3 // Done/Cancelled

  if (!dueDate) return 2 // Treat pending without due date as overdue backlog

  const today = new Date(now)
  today.setHours(0, 0, 0, 0)

  const tomorrow = new Date(now)
  tomorrow.setHours(24, 0, 0, 0)

  const time = dueDate.getTime()

  // 1. Today's Pending
  if (time >= today.getTime() && time < tomorrow.getTime()) return 0

  // 2. Upcoming Pending (Tomorrow or later)
  if (time >= tomorrow.getTime()) return 1

  // 3. Overdue Pending (Before today)
  return 2
}


interface UnifiedTask {
  id: string
  title: string
  dueDate: string | null
  taskFor: string | null
  leadId: string | null
  notes: string | null
  owner: { name: string; avatarUrl: string | null } | null
  status: string
  source: 'global' | 'lead'
}

function TasksTab({ page, setPage, pageSize, onPageSizeChange }: TabProps) {
  const navigate = useNavigate()
  const [search, setSearch] = useSessionState('activity_tasks_search', '')
  const [filters, setFilters] = useSessionState<UnifiedFilters>('activity_tasks_filters', EMPTY_UNIFIED_FILTERS)

  const { isSuperAdmin, isOrgAdmin, userOrgId } = useTenantTier()
  const { orgs, lobsForFilter, effectiveOrgIds, effectiveLobIds } = useOrgLobFilterData(filters, isSuperAdmin, isOrgAdmin, userOrgId)

  const filterDefs: FilterDef[] = useMemo(() => {
    const isNormalUser = !isSuperAdmin && !isOrgAdmin && !authStore.canViewLobWideData
    const defs = BASE_TASK_FILTER_DEFS.filter(d => isNormalUser ? d.type !== 'owner' : true)
    if (isSuperAdmin) {
      defs.push(
        { type: 'checkbox', key: 'orgId', label: 'Organisation', options: orgs.map(o => ({ label: o.name, value: o.id })) },
        { type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) },
      )
    } else if (isOrgAdmin) {
      defs.push({ type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) })
    }
    return defs
  }, [isSuperAdmin, isOrgAdmin, orgs, lobsForFilter])

  useEffect(() => {
    setPage(1)
  }, [search, filters, setPage])

  const apiFilters: LeadTaskFilters = useMemo(() => {
    const resolved = resolveDateRange(filters.datePreset, filters.dateFrom, filters.dateTo)
    return {
      search: search || undefined,
      statuses: filters.checkboxes.status?.length > 0 ? filters.checkboxes.status : undefined,
      ownerIds: filters.ownerIds?.length > 0 ? filters.ownerIds : undefined,
      orgIds: effectiveOrgIds,
      lobIds: effectiveLobIds,
      from: resolved.from,
      to: resolved.to,
    }
  }, [search, filters, effectiveOrgIds, effectiveLobIds])

  const { data, isLoading } = useQuery({
    queryKey: ['activity-tasks', page, pageSize, apiFilters],
    queryFn: () => leadTasksApi.getPagedForActivity(page, pageSize, apiFilters),
    staleTime: 30_000,
  })

  const leadTasks = useMemo(() => sortTasksForActivity(data?.items ?? []), [data?.items])
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const columns: Column[] = [
    {
      id: 'title',
      label: 'Title',
      minWidth: 200,
      render: (_: any, row: LeadTask) => (
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2F2F3A' }}>{row.title}</Typography>
      ),
    },
    {
      id: 'dueDate',
      label: 'Due Date & Time',
      minWidth: 160,
      render: (_: any, row: LeadTask) => (
        <Typography sx={{ fontSize: 13, color: row.dueDate ? '#2F2F3A' : '#9ca3af' }}>
          {row.dueDate ? fmtDateTime(row.dueDate) : '—'}
        </Typography>
      ),
    },
    {
      id: 'taskFor',
      label: 'Task For',
      minWidth: 140,
      render: (_: any, row: LeadTask) => {
        if (row.leadName && row.leadId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: themeColors.primary, fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/leads/${row.leadId}`)}
            >
              {row.leadName}
            </Typography>
          )
        } else if (row.dealName && row.dealId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: '#ca8a04', fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/deals/${row.dealId}`)}
            >
              {row.dealName}
            </Typography>
          )
        } else if (row.salesTitle && row.salesId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: '#16a34a', fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/sales/${row.salesId}`)}
            >
              {row.salesTitle}
            </Typography>
          )
        } else if (row.retentionCustomerName && row.retentionCustomerId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: '#9333ea', fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/customer-retention/${row.retentionCustomerId}`)}
            >
              {row.retentionCustomerName}
            </Typography>
          )
        } else if (row.contactName && row.contactId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: '#0284c7', fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/contacts/${row.contactId}`)}
            >
              {row.contactName}
            </Typography>
          )
        }
        return <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>—</Typography>
      },
    },
    {
      id: 'notes',
      label: 'Notes',
      minWidth: 180,
      render: (_: any, row: LeadTask) => (
        <Typography sx={{ fontSize: 13, color: row.description ? '#2F2F3A' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.description || '—'}
        </Typography>
      ),
    },
    {
      id: 'owner',
      label: 'Owner',
      minWidth: 130,
      render: (_: any, row: LeadTask) => row.assignedTo
        ? <UserCell name={row.assignedTo.name} avatarUrl={row.assignedTo.avatarUrl} />
        : <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>—</Typography>,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 110,
      render: (_: any, row: LeadTask) => {
        const s = TASK_STATUS[row.status] ?? TASK_STATUS.PENDING
        return <StatusChip label={s.label} color={s.color} bg={s.bg} />
      },
    },
  ]

  return (
    <>
      <ListToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search task title, lead/deal name…"
        filterDefs={filterDefs}
        filters={filters}
        onFilterChange={setFilters}
      />
      <Box sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataTable
          columns={columns}
          rows={leadTasks}
          isLoading={isLoading}
          rowKey={r => r.id}
          emptyMessage={search || countActiveFilters(filters) > 0 ? 'No tasks match your filters' : 'No tasks yet'}
          emptyHint={search || countActiveFilters(filters) > 0 ? 'Try adjusting your filters' : 'Tasks created from leads or the task board will appear here'}
          emptyIcon={<AssignmentIcon />}
          selectable={false}
          showRowNumbers={false}
          pagination={{
            page,
            pageSize,
            total,
            totalPages,
            onPageChange: setPage,
            onPageSizeChange,
            pageSizeOptions: [10, 20, 50, 100]
          }}
        />
      </Box>
    </>
  )
}

function EventsTab({ page, setPage, pageSize, onPageSizeChange }: TabProps) {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(EMPTY_UNIFIED_FILTERS)

  const { isSuperAdmin, isOrgAdmin, userOrgId } = useTenantTier()
  const { orgs, lobsForFilter, effectiveOrgIds, effectiveLobIds } = useOrgLobFilterData(filters, isSuperAdmin, isOrgAdmin, userOrgId)

  const filterDefs: FilterDef[] = useMemo(() => {
    const isNormalUser = !isSuperAdmin && !isOrgAdmin && !authStore.canViewLobWideData
    const defs = BASE_EVENT_FILTER_DEFS.filter(d => isNormalUser ? d.type !== 'owner' : true)
    if (isSuperAdmin) {
      defs.push(
        { type: 'checkbox', key: 'orgId', label: 'Organisation', options: orgs.map(o => ({ label: o.name, value: o.id })) },
        { type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) },
      )
    } else if (isOrgAdmin) {
      defs.push({ type: 'checkbox', key: 'lobId', label: 'LOB', options: lobsForFilter.map(l => ({ label: l.name, value: l.id })) })
    }
    return defs
  }, [isSuperAdmin, isOrgAdmin, orgs, lobsForFilter])

  useEffect(() => {
    setPage(1)
  }, [search, filters, setPage])

  const apiFilters: FollowUpFilters = useMemo(() => {
    const resolved = resolveDateRange(filters.datePreset, filters.dateFrom, filters.dateTo)
    return {
      search: search || undefined,
      types: filters.checkboxes.type?.length > 0 ? filters.checkboxes.type : undefined,
      statuses: filters.checkboxes.status?.length > 0 ? filters.checkboxes.status : undefined,
      ownerIds: filters.ownerIds?.length > 0 ? filters.ownerIds : undefined,
      orgIds: effectiveOrgIds,
      lobIds: effectiveLobIds,
      from: resolved.from,
      to: resolved.to,
    }
  }, [search, filters, effectiveOrgIds, effectiveLobIds])

  const { data, isLoading } = useQuery({
    queryKey: ['activity-events', page, pageSize, apiFilters],
    queryFn: () => leadFollowUpsApi.getPagedForActivity(page, pageSize, apiFilters),
    staleTime: 30_000,
  })

  const followUps = useMemo(() => sortEventsForActivity(data?.items ?? []), [data?.items])
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const columns: Column[] = [
    {
      id: 'title',
      label: 'Title',
      minWidth: 180,
      render: (_: any, row: LeadFollowUp) => (
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#2F2F3A' }}>
          {row.title || FU_TYPE_LABELS[row.type] || row.type}
        </Typography>
      ),
    },
    {
      id: 'scheduledAt',
      label: 'Event Date',
      minWidth: 160,
      render: (_: any, row: LeadFollowUp) => (
        <Typography sx={{ fontSize: 13, color: '#2F2F3A' }}>{fmtDateTime(row.scheduledAt)}</Typography>
      ),
    },
    {
      id: 'duration',
      label: 'Duration',
      minWidth: 90,
      render: (_: any, row: LeadFollowUp) => (
        <Typography sx={{ fontSize: 13, color: '#2F2F3A' }}>
          {calcDuration(row.scheduledAt, row.endTime)}
        </Typography>
      ),
    },
    {
      id: 'leadName',
      label: 'Related To',
      minWidth: 140,
      render: (_: any, row: LeadFollowUp) => {
        if (row.leadName && row.leadId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: themeColors.primary, fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/leads/${row.leadId}`)}
            >
              {row.leadName}
            </Typography>
          )
        } else if (row.dealName && row.dealId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: '#ca8a04', fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/deals/${row.dealId}`)}
            >
              {row.dealName}
            </Typography>
          )
        } else if (row.salesTitle && row.salesId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: '#16a34a', fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/sales/${row.salesId}`)}
            >
              {row.salesTitle}
            </Typography>
          )
        } else if (row.retentionCustomerName && row.retentionCustomerId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: '#9333ea', fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/customer-retention/${row.retentionCustomerId}`)}
            >
              {row.retentionCustomerName}
            </Typography>
          )
        } else if (row.contactName && row.contactId) {
          return (
            <Typography
              sx={{ fontSize: 13, color: '#0284c7', fontWeight: 500, cursor: 'pointer' }}
              onClick={() => navigate(`/contacts/${row.contactId}`)}
            >
              {row.contactName}
            </Typography>
          )
        }
        return <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>—</Typography>
      },
    },
    {
      id: 'assignedTo',
      label: 'Attendee',
      minWidth: 140,
      render: (_: any, row: LeadFollowUp) => row.assignedTo
        ? <UserCell name={row.assignedTo.name} avatarUrl={row.assignedTo.avatarUrl} />
        : <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>—</Typography>,
    },
    {
      id: 'createdBy',
      label: 'Owner',
      minWidth: 140,
      render: (_: any, row: LeadFollowUp) => row.createdBy
        ? <UserCell name={row.createdBy.name} avatarUrl={row.createdBy.avatarUrl} />
        : <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>—</Typography>,
    },
    {
      id: 'type',
      label: 'Type',
      minWidth: 100,
      render: (_: any, row: LeadFollowUp) => (
        <Typography sx={{ fontSize: 13, color: '#2F2F3A' }}>{FU_TYPE_LABELS[row.type] ?? row.type}</Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (_: any, row: LeadFollowUp) => {
        const s = FU_STATUS[row.status] ?? FU_STATUS.PENDING
        return <StatusChip label={s.label} color={s.color} bg={s.bg} />
      },
    },
  ]

  return (
    <>
      <ListToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search event title, lead/deal name…"
        filterDefs={filterDefs}
        filters={filters}
        onFilterChange={setFilters}
      />
      <Box sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataTable
          columns={columns}
          rows={followUps}
          isLoading={isLoading}
          rowKey={r => r.id}
          emptyMessage={search || countActiveFilters(filters) > 0 ? 'No events match your filters' : 'No events yet'}
          emptyHint={search || countActiveFilters(filters) > 0 ? 'Try adjusting your filters' : 'Follow-ups and meetings created from lead profiles will appear here'}
          emptyIcon={<EventIcon />}
          selectable={false}
          showRowNumbers={false}
          pagination={{
            page,
            pageSize,
            total,
            totalPages,
            onPageChange: setPage,
            onPageSizeChange,
            pageSizeOptions: [10, 20, 50, 100]
          }}
        />
      </Box>
    </>
  )
}

// ─── Tab config ───────────────────────────────────────────────────────────────

const TABS = [
  { label: 'Call', icon: <PhoneIcon sx={{ fontSize: 16 }} />, newLabel: 'Log Call' },
  { label: 'Email', icon: <EmailIcon sx={{ fontSize: 16 }} />, newLabel: 'New Email' },
  { label: 'Tasks', icon: <AssignmentIcon sx={{ fontSize: 16 }} />, newLabel: 'New Task' },
  { label: 'Events', icon: <EventIcon sx={{ fontSize: 16 }} />, newLabel: 'New Event' },
]

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ActivityPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useSessionState('activity_active_tab', 0)
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null)
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [selectedCall, setSelectedCall] = useState<CallLog | null>(null)
  const [isCallModalOpen, setIsCallModalOpen] = useState(false)

  // Pagination states
  const [callPage, setCallPage] = useState(1)
  const [emailPage, setEmailPage] = useState(1)
  const [taskPage, setTaskPage] = useState(1)
  const [eventPage, setEventPage] = useState(1)

  const { hasAction } = usePermission()
  const canCreate = hasAction('/activity', 'create')

  const [callPageSize, setCallPageSize] = useState(() => {
    const saved = localStorage.getItem('crm_activity_calls_page_size')
    return saved ? Number(saved) : 10
  })
  const [emailPageSize, setEmailPageSize] = useState(() => {
    const saved = localStorage.getItem('crm_activity_emails_page_size')
    return saved ? Number(saved) : 10
  })
  const [taskPageSize, setTaskPageSize] = useState(() => {
    const saved = localStorage.getItem('crm_activity_tasks_page_size')
    return saved ? Number(saved) : 10
  })
  const [eventPageSize, setEventPageSize] = useState(() => {
    const saved = localStorage.getItem('crm_activity_events_page_size')
    return saved ? Number(saved) : 10
  })

  const handleCallPageSizeChange = (newSize: number) => {
    setCallPageSize(newSize)
    localStorage.setItem('crm_activity_calls_page_size', String(newSize))
    setCallPage(1)
  }
  const handleEmailPageSizeChange = (newSize: number) => {
    setEmailPageSize(newSize)
    localStorage.setItem('crm_activity_emails_page_size', String(newSize))
    setEmailPage(1)
  }
  const handleTaskPageSizeChange = (newSize: number) => {
    setTaskPageSize(newSize)
    localStorage.setItem('crm_activity_tasks_page_size', String(newSize))
    setTaskPage(1)
  }
  const handleEventPageSizeChange = (newSize: number) => {
    setEventPageSize(newSize)
    localStorage.setItem('crm_activity_events_page_size', String(newSize))
    setEventPage(1)
  }

  function handleEmailRowClick(row: EmailLog) {
    setSelectedEmail(row)
    setIsEmailModalOpen(true)
  }

  function handleCallRowClick(row: CallLog) {
    setSelectedCall(row)
    setIsCallModalOpen(true)
  }

  // handleNew removed as button is disabled

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Activity"
        titleWeight={5000}
      // actions removed: topmost button disabled
      />

      <Box sx={{ px: 3, pb: 3, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: '1px solid #e5e7eb', mb: 2, flexShrink: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                fontSize: 13,
                fontWeight: 500,
                textTransform: 'none',
                color: '#64748b',
                px: 2,
                '&.Mui-selected': { color: themeColors.primary, fontWeight: 600 },
              },
              '& .MuiTabs-indicator': { backgroundColor: themeColors.primary },
            }}
          >
            {TABS.map((t, i) => (
              <Tab
                key={t.label}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {t.icon}
                    {t.label}
                  </Box>
                }
                value={i}
              />
            ))}
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {tab === 0 && <CallTab onRowClick={handleCallRowClick} page={callPage} setPage={setCallPage} pageSize={callPageSize} onPageSizeChange={handleCallPageSizeChange} />}
          {tab === 1 && <EmailTab onRowClick={handleEmailRowClick} page={emailPage} setPage={setEmailPage} pageSize={emailPageSize} onPageSizeChange={handleEmailPageSizeChange} />}
          {tab === 2 && <TasksTab page={taskPage} setPage={setTaskPage} pageSize={taskPageSize} onPageSizeChange={handleTaskPageSizeChange} />}
          {tab === 3 && <EventsTab page={eventPage} setPage={setEventPage} pageSize={eventPageSize} onPageSizeChange={handleEventPageSizeChange} />}
        </Box>
      </Box>

      {/* Detail Modals */}
      <EmailDetailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        email={selectedEmail}
      />
      <CallDetailModal
        open={isCallModalOpen}
        onClose={() => setIsCallModalOpen(false)}
        call={selectedCall}
      />
    </Box>
  )
}
